import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import {PricingProps, PricingTier} from "../utils/typesStripe";
import {SubscriptionTier, subscriptionTierFromDisplayName, subscriptionTierToDisplayName} from "../utils/types"
import {AuthContext} from "./AuthContext";
import {useContext} from "react";
import Stack from "@mui/material/Stack";

const tiers: PricingTier[] = [
  {
    title: subscriptionTierToDisplayName(SubscriptionTier.free),
    price: '0',
    description: [
      'Backtests vs. SPY with limited summary statistics¹',
      'Share backtest results',
      'Develop Algorithms from the Browser',
      'Fortune 500 Ticker Universe²',
      'Up to 10 algorithms',
      '2 Year Backtest',
      'Request new features and give feedback',
    ],
    buttonText: 'Get started',
    buttonVariant: 'outlined',
  },
  {
    title: subscriptionTierToDisplayName(SubscriptionTier.plus),
    // subheader: 'Most popular',
    price: '5',
    description: [
      'Backtests vs. SPY with full summary statistics¹',
      'Save and share backtest results',
      'AI Assistant',
      'Develop Algorithms from the Browser',
      'Fortune 500 Ticker Universe²',
      'Up to 200 algorithms',
      '6 Year Backtest',
      'Earlier access to new features',
      'Request new features and give feedback',
    ],
    buttonText: 'Get started',
    buttonVariant: 'contained',
  },
  {
    title: subscriptionTierToDisplayName(SubscriptionTier.premium),
    price: '10',
    description: [
      'Backtests vs. SPY with full summary statistics¹',
      'Save and share backtest results',
      'AI Assistant',
      'Develop Algorithms from the Browser',
      'Develop Algorithms Locally',
      'Fortune 500 Ticker Universe²',
      'Up to 500 algorithms',
      '10+ Year Backtest',
      'Earlier access to new features',
      'Prioritized feature requests and feedback',
    ],
    buttonText: 'Get Started',
    buttonVariant: 'outlined',
  },
];

export default function Pricing(props: PricingProps) {
  const authContext = useContext(AuthContext);
  const currentSubscriptionTierFromToken = authContext.getSubscriptionTier();
  const [selectedTier, setSelectedTier] = React.useState<string | null>(subscriptionTierToDisplayName(currentSubscriptionTierFromToken));

  const handleSelect = (tier: PricingTier) => {
    setSelectedTier(tier.title);
    if (props.onSelect) {
      props.onSelect(tier);
    }
  };

  return (
    <Container
      id="pricing"
      maxWidth="xl"
      sx={{
        pt: { xs: 4, sm: 12 },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: 3, sm: 6 },
      }}
    >
      <Box
        sx={{
          width: { sm: '100%', md: '60%' },
          textAlign: { sm: 'left', md: 'center' },
        }}
      >
        <Typography component="h2" variant="h4" color="text.primary" sx={{pb: 4}}>
          Pricing
        </Typography>
        <Typography variant="body1" color="text.secondary">
          The following plans are available to fit your needs. We've got you covered.
        </Typography>
      </Box>
      <Grid container spacing={3} sx={{ alignItems: 'center', justifyContent: 'center' }}>
        {tiers.map((tier) => {
          const isSelected = selectedTier === tier.title;
          const isPlus = subscriptionTierFromDisplayName(tier.title) === SubscriptionTier.plus;
          return (
            <Grid
              size={{ xs: 12, sm: 11, md: 4 }}
              key={tier.title}
            >
              <Card
                onClick={props.selectable ? () => handleSelect(tier) : undefined}
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  border: isSelected ? '2px solid' : isPlus ? '1px solid' : undefined,
                  borderColor: isSelected ? 'white' : isPlus ? 'primary.main' : undefined,
                  background: isPlus
                    ? 'radial-gradient(circle at 50% 0%, hsl(210, 100%, 16%), transparent)'
                    : undefined,
                  cursor: props.selectable ? 'pointer' : 'default',
                  transition: 'box-shadow 0.2s, border-color 0.2s',
                  ...(props.selectable && {
                    '&:hover': {
                      boxShadow: 6,
                    },
                  }),
                }}
              >
            <CardContent
              sx={{
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                  color: isPlus ? 'primary.light' : '',
                }}
              >
                <Typography component="h3" variant="h6">
                  {tier.title}
                </Typography>
                {isPlus && (
                  <Box
                    sx={{
                      py: 0.5,
                      px: 1,
                      borderRadius: '10px',
                      bgcolor: 'primary.main',
                      color: 'white',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {tier.subheader}
                  </Box>
                )}
              </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    color: isPlus ? 'primary.light' : '',
                  }}
                >
                  <Typography component="h3" variant="h2">
                    ${tier.price}
                  </Typography>
                  <Typography component="h3" variant="h6">
                    &nbsp; per month
                  </Typography>
                </Box>
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  {tier.description.map((line: string) => (
                    <Box
                      key={line}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <CheckCircleRoundedIcon
                        sx={{
                          width: 20,
                          color: isPlus ? 'primary.light' : 'primary.main',
                        }}
                      />
                      <Typography
                        variant="subtitle2"
                        component={'span'}
                        sx={{
                          color: isPlus ? 'primary.light' : undefined,
                        }}
                      >
                        {line}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
              <CardActions>
                {props.selectable ? (
                  <Button
                    fullWidth
                    variant={isSelected ? 'contained' : 'outlined'}
                    onClick={() => handleSelect(tier)}
                    sx={isSelected ? {
                      backgroundColor: 'white',
                      color: 'grey.900',
                      borderColor: 'white',
                      '&:hover': { backgroundColor: 'grey.100' },
                    } : undefined}
                  >
                    {isSelected ? '✓ Selected' : 'Select'}
                  </Button>
                ) : props.showButtons ? (
                  <Button
                    fullWidth
                    variant={tier.buttonVariant as 'outlined' | 'contained'}
                    component="a"
                    href={"/login"}
                  >
                    {tier.buttonText}
                  </Button>
                ) : null}
              </CardActions>
            </Card>
          </Grid>
        )})}
      </Grid>
      <Stack direction={"column"} spacing={0.25} sx={{alignSelf: 'flex-start'}}>
        <Typography variant="caption" color="text.secondary">
          ¹ Stocks (equities, daily bars) only — futures and options are not currently supported.
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ² Expansions planned for late 2026.
        </Typography>
      </Stack>
    </Container>
  );
}
